import React, { FC, PropsWithChildren } from "react";
import cx from "clsx";
import { cn } from "@/lib/utils";
// import { useTheme } from 'next-themes'

export type BlockWrapperBg =
  | "light"
  | "dark"
  | "muted"
  | "default"
  | "red";

interface BlockWrapperProps extends PropsWithChildren {
  className?: string;
  isInverted?: boolean;
  noPadding?: boolean;
  noYPadding?: boolean;
  isNarrow?: boolean;
  withPattern?: boolean;
  bgColor?: BlockWrapperBg;
  stickyTop?: boolean;
  stickyBottom?: boolean;
  noBottomMargin?: boolean;
}
const getClassForTheme = (
  isInverted: BlockWrapperProps["isInverted"],
  bgColor?: BlockWrapperProps["bgColor"]
) => {
  if (isInverted) return "bg-foreground text-foreground";
  if (bgColor === "dark") return "bg-secondary text-secondary-foreground";
  if (bgColor === "muted") return "bg-muted text-foreground";
  if (bgColor === "red") return "bg-primary text-primary-foreground";
  return "bg-background text-foreground";
};

function getTintedScopeClass(bgColor?: BlockWrapperProps["bgColor"]): string | null {
  if (bgColor === "dark") return "block-section-dark";
  if (bgColor === "red") return "block-section-red";
  return null;
}

/** 1440px content column — width only, без боковых линий. */
export const CONTENT_WIDTH = "mx-auto w-full max-w-[1440px]";

/**
 * Вертикальные линии рамки 1px (#6F6F70 / border-border).
 * Псевдоэлементы вместо border-x — border-x + gap-px bg-border давали 2px справа.
 */
export const CONTENT_SIDE_LINES =
  "relative before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:z-50 before:w-px before:bg-border after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:z-50 after:w-px after:bg-border";

/**
 * Вертикальные линии 1440px на всю высоту контейнера без ограничения ширины дочерних блоков.
 * Для `<main>`: фоны секций и горизонтальные разделители остаются full-bleed.
 */
export const CONTENT_SIDE_LINES_TRACK = cn(
  CONTENT_WIDTH,
  CONTENT_SIDE_LINES,
  "pointer-events-none absolute inset-y-0 left-1/2 z-[5] -translate-x-1/2"
);

/**
 * Колонка 1440px с вертикальными линиями (Figma main-1440).
 * На header / footer — не дублировать в BlockWrapper и сетках.
 * На `<main>` — `CONTENT_SIDE_LINES_TRACK` (линии без ограничения ширины блоков).
 */
export const CONTENT_FRAME = `${CONTENT_WIDTH} ${CONTENT_SIDE_LINES}`;

export const BLOCK_WIDTH = CONTENT_WIDTH;

export const BlockWrapper: FC<BlockWrapperProps> = ({
  children,
  className,
  isInverted,
  noPadding,
  noYPadding,
  isNarrow,
  withPattern = false,
  bgColor,
  stickyTop,
  stickyBottom,
  noBottomMargin,
}) => {
  // const { theme } = useTheme()

  const contentFrameClass = isNarrow
    ? cn("mx-auto w-full max-w-[400px]", CONTENT_SIDE_LINES)
    : CONTENT_FRAME;

  return (
    <div
      className={[
        getClassForTheme(isInverted, bgColor),
        getTintedScopeClass(bgColor),
        "relative flex flex-col overflow-visible",
        stickyTop && "sticky top-0 z-10",
        stickyBottom && "sticky bottom-0 z-10",
        className,
        noBottomMargin && "mb-0",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {withPattern && (
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 z-0" />
          <div className={cx("stars", "absolute inset-0 z-0")} />
        </div>
      )}
      <div
        className={cx(
          contentFrameClass,
          "h-full overflow-x-visible z-10",
          !noPadding && "p-4 py-10 sm:p-10 md:p-12 lg:p-16",
          noYPadding && "py-0",
        )}
      >
        {children}
      </div>
    </div>
  );
};
